/**
 * The story video player's pure half (handoff Part A §4b / Part D): tap zones, timings, the
 * portrait rule, the storyboard VTT. No DOM, no React — the player reads these, the tests
 * read all of it, and `StoryViewer` borrows `tapZone` so the Portals' own gestures land in
 * the same four zones.
 */
import { SAFE_B } from '../../utils/safeArea';

/** Hold this long and the clip pauses; shorter is a tap. */
export const HOLD_MS = 220;
/** The scrub bar fades this long after the last touch or skip. */
export const BAR_HIDE_MS = 2500;
/** Let the first frame land, then the tips settle in. */
export const COACH_DELAY_MS = 900;
/** A touch on a hidden bar only reveals it; it scrubs once the finger has moved this far. */
export const SCRUB_DEAD_PX = 6;
/** The storyboard preview: its width and how far from each edge it is kept. */
export const PREVIEW_W = 112;
export const PREVIEW_EDGE_PX = 58;
/** Seeks never land on the very last frame: the clip is let finish and advance on its own. */
export const END_GUARD_SEC = 0.1;
/** The per-account flag for the first-video tips. */
export const VIDEO_TIPS_PREF = 'videoTips.seen';

/** Edge 20% = previous / next item; the inner 30% halves rewind / forward. */
export const EDGE = 0.2;

/**
 * Which zone a tap at `fx` (0..1 across the stage) fell in. With no edge zones (a lone video
 * message) the whole stage splits into rewind / forward.
 */
export function tapZone(fx, edgeZones = true) {
  const x = Math.min(1, Math.max(0, Number.isFinite(fx) ? fx : 0.5));
  if (edgeZones) {
    if (x < EDGE) return 'prev';
    if (x > 1 - EDGE) return 'next';
  }
  return x < 0.5 ? 'rewind' : 'forward';
}

/** A clip taller than 1.5× its width fills the stage; anything else letterboxes. */
export function isPortrait(width, height) {
  return width > 0 && height > 0 && height / width > 1.5;
}

/** Where a seek lands: inside the clip, short of its last frame. */
export function clampSeek(t, durationSec) {
  if (!(durationSec > 0)) return Math.max(0, t || 0);
  return Math.min(Math.max(t || 0, 0), Math.max(0, durationSec - END_GUARD_SEC));
}

/** The preview's `left` as a CSS clamp — centred on the finger, never off the edge. */
export function previewLeft(frac) {
  const pct = (Math.min(1, Math.max(0, frac || 0)) * 100).toFixed(2);
  return `clamp(${PREVIEW_EDGE_PX}px, ${pct}%, calc(100% - ${PREVIEW_EDGE_PX}px))`;
}

/** `1:42` — a clock the way the pills print it. */
export function fmtClock(sec) {
  const s = Math.max(0, Math.floor(Number.isFinite(sec) ? sec : 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** The storyboard's frame interval for a clip of this length (2 s / 5 s / 10 s). */
export function storyboardIntervalSec(durationSec) {
  if (durationSec < 120) return 2;
  if (durationSec < 600) return 5;
  return 10;
}

/**
 * Parse a WebVTT storyboard into cues: `[{ start, end, url, xywh }]`. A cue's text is one URL,
 * optionally with a `#xywh=x,y,w,h` sprite fragment. Tolerant of blank lines and a header.
 */
export function parseStoryboard(text) {
  const lines = String(text || '').replace(/\r/g, '').split('\n');
  const cues = [];
  const toSec = (stamp) => {
    const parts = stamp.trim().split(':').map(Number);
    if (parts.some((n) => !Number.isFinite(n))) return null;
    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
    if (parts.length === 2) return parts[0] * 60 + parts[1];
    return parts[0];
  };
  for (let i = 0; i < lines.length; i += 1) {
    const m = lines[i].match(/^\s*([\d:.]+)\s+-->\s+([\d:.]+)/);
    if (!m) continue;
    const start = toSec(m[1]);
    const end = toSec(m[2]);
    let j = i + 1;
    while (j < lines.length && !lines[j].trim()) j += 1;
    const raw = (lines[j] || '').trim();
    if (start == null || end == null || !raw) continue;
    const hash = raw.indexOf('#xywh=');
    let url = raw;
    let xywh = null;
    if (hash >= 0) {
      url = raw.slice(0, hash);
      const nums = raw.slice(hash + 6).split(',').map(Number);
      if (nums.length === 4 && nums.every(Number.isFinite)) xywh = { x: nums[0], y: nums[1], w: nums[2], h: nums[3] };
    }
    cues.push({ start, end, url, xywh });
    i = j;
  }
  return cues;
}

/** The cue under `sec` — or the last one that started, so the preview never goes blank. */
export function cueAt(cues, sec) {
  if (!cues || !cues.length) return null;
  const t = Number.isFinite(sec) ? sec : 0;
  let hit = null;
  for (let i = 0; i < cues.length; i += 1) {
    if (cues[i].start <= t) hit = cues[i]; else break;
  }
  return hit || cues[0];
}

/** The indexes to prefetch around the knob: about six frames centred on the one in view. */
export function prefetchWindow(cues, sec, span = 3) {
  if (!cues || !cues.length) return [];
  const cur = cueAt(cues, sec);
  const i = Math.max(0, cues.indexOf(cur));
  const from = Math.max(0, i - span);
  const to = Math.min(cues.length - 1, i + span);
  const out = [];
  for (let k = from; k <= to; k += 1) out.push(k);
  return out;
}

/**
 * Where the scrub strip sits above the bottom edge: the host's own chrome (`bottomInset`, a
 * number of px or any CSS length) plus the system bar's strip, because Android draws its
 * navigation buttons over the WebView and a control under them is a control the app never
 * hears about. A host that has already lifted the player's box off that strip passes
 * `systemBarInset: false` so it is counted once.
 *
 * It comes back as a string rather than being written inline because jsdom's CSSOM drops any
 * `calc()` carrying `env()` — the declaration disappears from the DOM, so it can only be
 * checked here.
 */
export function bottomClearance(bottomInset = 0, systemBarInset = true) {
  const own = typeof bottomInset === 'number' ? `${bottomInset}px` : (bottomInset || '0px');
  const base = own === '0px' ? '6px' : own;
  return systemBarInset ? `calc(${base} + ${SAFE_B})` : base;
}
